import React, { useEffect, useState, useCallback } from "react";
import { API_BASE_URL } from "../../../config";
import CreativeChangeReview from "./CreativeChangeReview";

// Approve/deny/clear all ignored the response. Approve returns 409 when
// another booking already holds one of those screen-days (the UNIQUE
// constraint doing its job), and clear returns 409 when the booking still owes
// the customer a refund -- deleting it would destroy the only record of the
// debt. Both looked identical to success: the list refreshed and the row was
// simply unchanged.

const TERMINAL = ["cancelled", "rejected", "denied"];

// Payment states that mean money is owed and nobody has moved it yet. These
// are the rows a human has to settle, so they should not look like ordinary
// ones.
const OWES = ["refund_pending", "refund_failed", "refund_manual", "disputed"];

function money(value) {
    if (value === null || value === undefined || value === "") return "—";
    const n = Number(value);
    return isNaN(n) ? "—" : `$${n.toFixed(2)}`;
}

function when(value) {
    if (!value) return "—";
    const d = new Date(value);
    return isNaN(d) ? "—" : d.toLocaleDateString();
}

function readable(value) {
    if (!value) return "—";
    return value.replace(/_/g, " ");
}

function statusTone(status) {
    if (status === "approved" || status === "completed") return "wb-badge--ok";
    if (status === "pending") return "wb-badge--warn";
    if (TERMINAL.includes(status)) return "wb-badge--danger";
    return "";
}

export default function VendorRequestsDashboard() {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [busyId, setBusyId] = useState(null);
    const [error, setError] = useState("");
    const [notice, setNotice] = useState("");

    const token = localStorage.getItem("token");

    const fetchRequests = useCallback(async () => {
        setLoading(true);
        try {
            const res = await fetch(`${API_BASE_URL}/api/vendor/requests`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json();
            setRequests(data.requests || []);
        } catch {
            setRequests([]);
        }
        setLoading(false);
    }, [token]);

    useEffect(() => { fetchRequests(); }, [fetchRequests]);

    async function handleAction(requestId, action) {
        setBusyId(requestId);
        setError("");
        setNotice("");
        try {
            const res = await fetch(`${API_BASE_URL}/api/vendor/requests/${requestId}/${action}`, {
                method: "POST",
                headers: { Authorization: `Bearer ${token}` },
            });
            const data = await res.json().catch(() => ({}));
            if (!res.ok) {
                setError(data.message || `Could not ${action} this booking.`);
            } else {
                setNotice(data.message || "Done.");
            }
        } catch {
            setError("Network error. Nothing changed.");
        }
        setBusyId(null);
        fetchRequests();
    }

    if (loading) {
        return (
            <div className="wb-page">
                <p className="wb-sub">Loading requests…</p>
            </div>
        );
    }

    return (
        <div className="wb-page">
            <header className="wb-page-head">
                <h1>Booking requests</h1>
                <p className="wb-sub">
                    Every booking on your screens. Answer the pending ones — nothing
                    plays until you do.
                </p>
            </header>

            {/* Renders nothing when there is nothing waiting. A swap on a
                booking that is already sold is a separate decision from the
                booking itself, and it does not reach the screen until it is
                answered here. */}
            <CreativeChangeReview onReviewed={fetchRequests} />

            {error && <div className="wb-note wb-note--danger">{error}</div>}
            {notice && !error && <div className="wb-note wb-note--ok">{notice}</div>}

            {requests.length === 0 ? (
                <div className="wb-empty">No booking requests yet.</div>
            ) : (
                <div className="wb-card wb-card--flush">
                    <div className="wb-table-wrap">
                        <table className="wb-table">
                            <thead>
                                <tr>
                                    <th>Screen</th>
                                    <th>Customer</th>
                                    <th>Dates</th>
                                    <th className="wb-num">Per day</th>
                                    <th className="wb-num">Total</th>
                                    <th>Status</th>
                                    <th>Payment</th>
                                    <th>Actions</th>
                                </tr>
                            </thead>
                            <tbody>
                                {requests.map((req) => (
                                    <tr key={req.id}>
                                        <td>{req.device_name || `#${req.device_id}`}</td>
                                        <td>#{req.customer_id}</td>
                                        <td>{when(req.start_time)} – {when(req.end_time)}</td>
                                        <td className="wb-num">{money(req.proposed_rate)}</td>
                                        <td className="wb-num">{money(req.amount)}</td>
                                        <td>
                                            <span className={"wb-badge " + statusTone(req.status)}>
                                                {readable(req.status)}
                                            </span>
                                        </td>
                                        <td>
                                            <span className={
                                                "wb-badge "
                                                + (OWES.includes(req.payment_status) ? "wb-badge--danger" : "")
                                            }>
                                                {readable(req.payment_status)}
                                            </span>
                                        </td>
                                        <td style={{ whiteSpace: "nowrap" }}>
                                            {req.status === "pending" && (
                                                <div className="wb-row" style={{ gap: "var(--s-2)" }}>
                                                    <button
                                                        type="button"
                                                        className="wb-btn wb-btn--primary wb-btn--sm"
                                                        disabled={busyId === req.id}
                                                        onClick={() => handleAction(req.id, "approve")}
                                                    >
                                                        Approve
                                                    </button>
                                                    {/* Rejecting refunds the customer and cannot be
                                                     *  undone, so it is --danger rather than a second
                                                     *  button that looks like Approve. */}
                                                    <button
                                                        type="button"
                                                        className="wb-btn wb-btn--danger wb-btn--sm"
                                                        disabled={busyId === req.id}
                                                        onClick={() => handleAction(req.id, "deny")}
                                                    >
                                                        Deny
                                                    </button>
                                                </div>
                                            )}
                                            {/* "denied" is the legacy spelling; the API now returns
                                                "rejected" for both. Kept here so rows written before
                                                the migration still show a Clear button. */}
                                            {TERMINAL.includes(req.status) && (
                                                <button
                                                    type="button"
                                                    className="wb-btn wb-btn--secondary wb-btn--sm"
                                                    disabled={busyId === req.id}
                                                    onClick={() => handleAction(req.id, "clear")}
                                                >
                                                    Clear
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}
        </div>
    );
}
